/**
 * @module bridges
 * @package @geenius/adapters/react-mantine
 * @description Mantine-native bridge components for adapter loading, error,
 * and notification surfaces. Adapter contracts remain delegated to the React
 * reference variant; this module only owns Mantine rendering affordances.
 */

"use client";

import type {
  AlertProps,
  LoaderProps,
  MantineColor,
  NotificationProps,
  ProgressProps,
  StackProps,
} from "@mantine/core";
import {
  Component,
  type ErrorInfo,
  type JSX,
  type ReactNode,
  useEffect,
  useState,
} from "react";

type MantineCore = typeof import("@mantine/core");
type DataAttributeProps = {
  [key: `data-${string}`]: string | number | boolean | undefined;
};

let cachedMantineCore: MantineCore | null = null;

function useMantineCore(): MantineCore | null {
  const [core, setCore] = useState<MantineCore | null>(cachedMantineCore);

  useEffect(() => {
    if (cachedMantineCore || typeof document === "undefined") {
      setCore(cachedMantineCore);
      return;
    }

    let active = true;

    void import("@mantine/core").then((module) => {
      if (active) {
        cachedMantineCore = module;
        setCore(module);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  return core;
}

function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

function normalizeProgress(progress: number | undefined): number | undefined {
  if (progress === undefined) {
    return undefined;
  }

  return Math.min(100, Math.max(0, progress));
}

/**
 * Props accepted by the Mantine async-adapter loading bridge.
 *
 * @property loading Controls whether the loading affordance or children render.
 * @property isLoading Alias for `loading` used by async adapter states.
 * @property label Accessible and visible loading label.
 * @property description Optional supporting loading copy.
 * @property progress Optional determinate progress from 0 through 100.
 * @property variant Chooses Mantine loader or progress primitives.
 * @property children Content rendered when loading is explicitly false.
 * @property color Mantine color applied to the loading primitive.
 * @property stackProps Props forwarded to the Mantine Stack wrapper.
 * @property loaderProps Props forwarded to the Mantine Loader primitive.
 * @property progressProps Props forwarded to the Mantine Progress primitive.
 */
export interface LoadingBridgeProps {
  loading?: boolean;
  isLoading?: boolean;
  label?: ReactNode;
  description?: ReactNode;
  progress?: number;
  variant?: "loader" | "progress";
  children?: ReactNode;
  color?: MantineColor;
  stackProps?: Omit<StackProps, "children"> & DataAttributeProps;
  loaderProps?: LoaderProps;
  progressProps?: Omit<ProgressProps, "value">;
}

/**
 * Renders a Mantine loading affordance for async adapter operations.
 *
 * @param props Loading bridge props.
 * @returns Children when idle, otherwise an accessible Mantine loading surface.
 */
export function LoadingBridge({
  loading,
  isLoading,
  label = "Loading adapters",
  description,
  progress,
  variant = "loader",
  children = null,
  color,
  stackProps,
  loaderProps,
  progressProps,
}: LoadingBridgeProps): JSX.Element {
  const mantine = useMantineCore();
  const active = isLoading ?? loading ?? true;

  if (!active) {
    return <>{children}</>;
  }

  const normalizedProgress = normalizeProgress(progress);
  const progressLabel = typeof label === "string" ? label : "Loading adapters";
  const {
    animated,
    striped,
    "aria-label": progressAriaLabel,
    className: progressClassName,
  } = progressProps ?? {};
  const { className: loaderClassName } = loaderProps ?? {};
  const { className: stackClassName } = stackProps ?? {};
  const progressIsIndeterminate = normalizedProgress === undefined;

  if (mantine) {
    const { Loader, Progress, Stack, Text } = mantine;

    return (
      <Stack
        {...stackProps}
        align={variant === "progress" ? "stretch" : "center"}
        aria-busy="true"
        aria-live="polite"
        gap="xs"
        role="status"
      >
        {variant === "progress" ? (
          <Progress
            {...progressProps}
            aria-label={progressAriaLabel ?? progressLabel}
            animated={animated ?? progressIsIndeterminate}
            color={color}
            striped={striped ?? progressIsIndeterminate}
            value={normalizedProgress ?? 100}
          />
        ) : (
          <Loader {...loaderProps} aria-label={progressLabel} color={color} />
        )}
        {label ? (
          <Text component="span" fw={500} size="sm">
            {label}
          </Text>
        ) : null}
        {description ? (
          <Text c="var(--mantine-color-text)" component="span" size="xs">
            {description}
          </Text>
        ) : null}
      </Stack>
    );
  }

  return (
    <div
      aria-busy="true"
      aria-live="polite"
      className={cx(
        "gn-adapters-mantine-loading",
        variant === "progress"
          ? "grid gap-2"
          : "flex flex-col items-center gap-2",
        stackClassName,
      )}
      data-color={color}
      data-gap="xs"
      role="status"
    >
      {variant === "progress" ? (
        <progress
          aria-label={progressAriaLabel ?? progressLabel}
          aria-valuenow={normalizedProgress}
          className={cx(
            "gn-adapters-mantine-progress",
            progressIsIndeterminate && "gn-adapters-mantine-progress--busy",
            progressClassName,
          )}
          data-animated={animated ?? progressIsIndeterminate}
          data-color={color}
          data-striped={striped ?? progressIsIndeterminate}
          max={100}
          value={normalizedProgress ?? undefined}
        />
      ) : (
        <span
          aria-label={progressLabel}
          className={cx("gn-adapters-mantine-loader", loaderClassName)}
          data-color={color}
          role="progressbar"
        />
      )}
      {label ? (
        <span className="gn-adapters-mantine-loading__label">{label}</span>
      ) : null}
      {description ? (
        <span className="gn-adapters-mantine-loading__description">
          {description}
        </span>
      ) : null}
    </div>
  );
}

type ErrorBoundaryFallback =
  | ReactNode
  | ((error: Error, reset: () => void) => ReactNode);

/**
 * Props accepted by the Mantine adapter error boundary bridge.
 *
 * @property children React subtree protected by the boundary.
 * @property error Optional controlled error for async adapter workflows.
 * @property fallback Optional custom fallback renderer or node.
 * @property onError Callback invoked with the caught error and React error info.
 * @property onReset Called before local error state is reset.
 * @property title Heading displayed in the default Mantine alert fallback.
 * @property description Supporting copy displayed in the default fallback.
 * @property resetLabel Button label for retrying the protected subtree.
 * @property color Mantine color applied to the default fallback.
 * @property alertProps Additional props forwarded to the Mantine Alert.
 */
export interface ErrorBoundaryBridgeProps {
  children?: ReactNode;
  error?: unknown;
  fallback?: ErrorBoundaryFallback;
  onError?: (error: Error, info: ErrorInfo) => void;
  onReset?: () => void;
  title?: ReactNode;
  description?: ReactNode;
  resetLabel?: ReactNode;
  color?: MantineColor;
  alertProps?: Omit<AlertProps, "children" | "color" | "role" | "title"> &
    DataAttributeProps;
}

interface ErrorBoundaryBridgeState {
  caughtError: Error | null;
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(typeof error === "string" ? error : "Adapter bridge error");
}

interface DefaultErrorFallbackProps {
  bridgeProps: ErrorBoundaryBridgeProps;
  error: Error;
  reset: () => void;
}

function DefaultErrorFallback({
  bridgeProps,
  error,
  reset,
}: DefaultErrorFallbackProps): JSX.Element {
  const mantine = useMantineCore();
  const color = bridgeProps.color ?? "red";

  if (mantine) {
    const { Alert, Button, Text } = mantine;

    return (
      <Alert
        {...bridgeProps.alertProps}
        color={color}
        role="alert"
        title={bridgeProps.title ?? "Adapter error"}
      >
        {bridgeProps.description ? (
          <Text component="p" mb="xs" size="sm">
            {bridgeProps.description}
          </Text>
        ) : null}
        <Text component="span" size="sm">
          {error.message}
        </Text>
        <Button
          color={color}
          mt="sm"
          onClick={reset}
          size="xs"
          type="button"
          variant="light"
        >
          {bridgeProps.resetLabel ?? "Retry"}
        </Button>
      </Alert>
    );
  }

  const { className: alertClassName } = bridgeProps.alertProps ?? {};

  return (
    <div
      className={cx("gn-adapters-mantine-alert", alertClassName)}
      data-color={color}
      role="alert"
    >
      <div className="gn-adapters-mantine-alert__title">
        {bridgeProps.title ?? "Adapter error"}
      </div>
      {bridgeProps.description ? (
        <p className="gn-adapters-mantine-alert__description">
          {bridgeProps.description}
        </p>
      ) : null}
      <span className="gn-adapters-mantine-alert__message">
        {error.message}
      </span>
      <button
        className="gn-adapters-mantine-alert__reset"
        data-color={color}
        onClick={reset}
        type="button"
      >
        {bridgeProps.resetLabel ?? "Retry"}
      </button>
    </div>
  );
}

/**
 * Catches render-time failures and displays a Mantine-native adapter error state.
 */
export class ErrorBoundaryBridge extends Component<
  ErrorBoundaryBridgeProps,
  ErrorBoundaryBridgeState
> {
  state: ErrorBoundaryBridgeState = { caughtError: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryBridgeState {
    return { caughtError: normalizeError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(normalizeError(error), info);
  }

  private reset = (): void => {
    this.props.onReset?.();
    this.setState({ caughtError: null });
  };

  render(): ReactNode {
    const error =
      this.props.error !== undefined
        ? normalizeError(this.props.error)
        : this.state.caughtError;

    if (!error) {
      return this.props.children;
    }

    if (typeof this.props.fallback === "function") {
      return this.props.fallback(error, this.reset);
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <DefaultErrorFallback
        bridgeProps={this.props}
        error={error}
        reset={this.reset}
      />
    );
  }
}

export type ToastBridgeVariant =
  | "default"
  | "info"
  | "success"
  | "warning"
  | "error";

const toastColorByVariant = {
  default: undefined,
  info: "blue",
  success: "green",
  warning: "yellow",
  error: "red",
} satisfies Record<ToastBridgeVariant, MantineColor | undefined>;

/**
 * Props accepted by the Mantine telemetry/notification toast bridge.
 *
 * @property open Whether the toast is visible.
 * @property message Message rendered inside the Mantine notification.
 * @property title Optional notification title.
 * @property variant Semantic toast variant mapped to Mantine colors.
 * @property color Explicit Mantine color override.
 * @property onClose Called when the close button is clicked.
 * @property withCloseButton Controls whether the Mantine close button renders.
 * @property icon Optional notification icon.
 * @property loading Replaces the notification color line/icon with Mantine Loader.
 * @property notificationProps Additional Mantine Notification props.
 * @property children Optional extra notification content.
 */
export interface ToastBridgeProps {
  open?: boolean;
  message?: ReactNode;
  title?: ReactNode;
  variant?: ToastBridgeVariant;
  color?: MantineColor;
  onClose?: () => void;
  withCloseButton?: boolean;
  icon?: ReactNode;
  loading?: boolean;
  notificationProps?: Omit<
    NotificationProps,
    | "children"
    | "color"
    | "icon"
    | "loading"
    | "onClose"
    | "role"
    | "title"
    | "withCloseButton"
  > &
    DataAttributeProps;
  children?: ReactNode;
}

/**
 * Renders adapter telemetry or notification messages through Mantine Notification.
 *
 * @param props Toast bridge props.
 * @returns A Mantine notification when open, otherwise `null`.
 */
export function ToastBridge({
  open = true,
  message,
  title,
  variant = "info",
  color,
  onClose,
  withCloseButton = true,
  icon,
  loading,
  notificationProps,
  children,
}: ToastBridgeProps): JSX.Element | null {
  const mantine = useMantineCore();

  if (!open) {
    return null;
  }

  const role = variant === "error" ? "alert" : "status";
  const { closeButtonProps: notificationCloseButtonProps } =
    notificationProps ?? {};

  if (mantine) {
    const { Notification, Text } = mantine;

    return (
      <Notification
        {...notificationProps}
        aria-live={role === "alert" ? "assertive" : "polite"}
        closeButtonProps={{
          "aria-label": "Close notification",
          ...notificationCloseButtonProps,
        }}
        color={color ?? toastColorByVariant[variant]}
        data-testid="gn-adapters-mantine-toast-bridge"
        icon={icon}
        loading={loading}
        onClose={onClose}
        role={role}
        title={title}
        withCloseButton={withCloseButton}
      >
        <Text c="var(--mantine-color-text)" component="span" size="sm">
          {message}
          {children}
        </Text>
      </Notification>
    );
  }

  const { className: notificationClassName } = notificationProps ?? {};

  return (
    <div
      aria-live={role === "alert" ? "assertive" : "polite"}
      className={cx("gn-adapters-mantine-notification", notificationClassName)}
      data-color={color ?? toastColorByVariant[variant]}
      data-testid="gn-adapters-mantine-toast-bridge"
      data-loading={loading}
      role={role}
    >
      {icon ? (
        <span className="gn-adapters-mantine-notification__icon">{icon}</span>
      ) : null}
      {title ? (
        <div className="gn-adapters-mantine-notification__title">{title}</div>
      ) : null}
      {message}
      {children}
      {withCloseButton ? (
        <button
          aria-label="Close notification"
          className="gn-adapters-mantine-notification__close"
          onClick={onClose}
          type="button"
        />
      ) : null}
    </div>
  );
}
